import { action, observable } from 'mobx';
import * as d3 from 'd3';
import { IPlotData } from '../../../models/EFH2/plotData';
// Plot Window Dimensions
class Dimensions {
  margins: any;
  aspectRatio: number;
  fullHeight: number;
  height: number;
  constructor(margins: any, height: number) {
    this.margins = margins;
    this.aspectRatio = 1.6180339887;
    this.fullHeight = height + margins.top + margins.bottom;
    this.height = height;
  }
  get fullWidth() {
    return this.aspectRatio * this.fullHeight;
  }
  get width() {
    return this.fullWidth - this.margins.right - this.margins.left;
  }
  // 1.4285714285714285714285714285714
}

export class D3LinePlotVM {
  @observable props;
  constructor(props: any) {
    this.props = props;
  }
  @observable plotData: IPlotData = {};
  @observable selectAllHydrographs = false;
  @observable viewBox = '0 0 750 525';
  @observable colors = ['#d4450a', '#fcc02c', '#6ae179', '#03bde5', '#f89545', '#c25501', '#0654e1', '#02aa17', '#ff01df'];
  @observable dimensions = new Dimensions(5, 5);
  @action.bound initPlotArea() {
    this.dimensions = new Dimensions(this.props.windowOptions.margins, this.props.windowOptions.height);
    d3.select('#' + this.props.targetDivID)
      .selectAll('svg')
      .remove();

    d3.select('#' + this.props.targetDivID)
      .append('svg')
      .attr('width', '100%')
      .attr('tabindex', 0)
      .attr('aria-label', this.props.windowOptions.ariaLabel)
      .attr('viewBox', this.viewBox);

    const xScale = d3
      .scaleLinear()
      .range([this.dimensions.margins.left, this.dimensions.fullWidth - this.dimensions.margins.right])
      .domain([0, 10]);

    const yScale = d3
      .scaleLinear()
      .range([this.dimensions.height - this.dimensions.margins.bottom, this.dimensions.margins.top])
      .domain([0, 10]);

    const hideAxisNumbers = true;
    this.drawAxisAndLabels(xScale, yScale, hideAxisNumbers);
  }
  // Plotting Functions
  @action.bound drawLinePlot(plotData: { any: [] }, config: { targetElementID: any; windowOptions?: any }) {
    const labels = Object.keys(plotData);
    const svg = d3.select('#' + config.targetElementID).select('svg');
    if (labels.length === 0) {
      return;
    }
    if (labels.every(label => plotData[label].length === 0)) { // the hydrograph is not displayed if we only check first item on label
      return;
    }
    if (config.targetElementID === undefined || config.targetElementID === '') {
      d3.select('#loader').remove();
      return;
    }

    const line_height = 15;
    const char_spacing = 10;
    let labelSpacing = 0;
    const labelSpacingPx: number[] = [];
    for (const label of labels) {
      labelSpacingPx.push(labelSpacing);
      labelSpacing = labelSpacing + label.length * char_spacing + 45;
    }
    const legendBox = svg
      .append('g')
      .attr('class', 'legend')
      .attr(
        'transform',
        'translate(' + (30 + this.dimensions.margins.left) + ',' + (this.dimensions.fullHeight - line_height) + ')'
      );

    legendBox.append('text').attr('dy', '1em').style('font-size', '10px');

    legendBox
      .append('g')
      .selectAll('.key')
      .data(labels)
      .enter()
      .append('text')
      .text(function (d: any) {
        return d;
      })
      .attr('transform', function (d: any, i: string | number) {
        return 'translate(' + (+i > 6 ? labelSpacingPx[+i-7] : labelSpacingPx[i]) + ',' + (+i > 6 ? line_height + 15 : 0) + ')';
      });

    legendBox
      .append('g')
      .style('font-size', '12px')
      .selectAll('.key')
      .data(labels)
      .enter()
      .append('circle')
      .attr('cy', function (d: any, i: string | number) {
        return (+i > 6 ? line_height + 15 - 6 : -6)
      })
      .attr('cx', function (d: any, i: string | number) {
        return (+i > 6 ? labelSpacingPx[+i-7] - 15 : labelSpacingPx[i] - 15);
      })
      .attr('r', '0.8em')
      .style('fill', (d: any, i: string | number) => {
        return this.colors[i];
      });

    // Draw all on same y and x axis
    // y goes from 0 - max
    let maxYValue = 0;
    let maxXValue = 0;
    let minXValue = 999999;
    for (const key in plotData) {
      // const yPoints = plotData[key].map((cv: []) => parseFloat(cv));
      const yMax = d3.max(plotData[key], function (d: any) {
        return +d[1];
      });
      const xMax = d3.max(plotData[key], function (d: any) {
        return +d[0];
      });
      const xMin = d3.min(plotData[key], function (d: any) {
        return +d[0];
      });
      if (yMax !== undefined && yMax > maxYValue) {
        maxYValue = yMax;
      }
      if (xMax !== undefined && xMax > maxXValue) {
        maxXValue = xMax;
      }
      if (xMin !== undefined && xMin < minXValue) {
        minXValue = xMin;
      }
    }
    const xScale = d3
      .scaleLinear()
      .range([this.dimensions.margins.left, this.dimensions.fullWidth - this.dimensions.margins.right])
      .domain([minXValue, maxXValue]);

    const yScale = d3
      .scaleLinear()
      .range([this.dimensions.height - this.dimensions.margins.bottom, this.dimensions.margins.top])
      .domain([0, maxYValue + maxYValue / 10]);

    this.drawAxisAndLabels(xScale, yScale, false);
    // draw each line
    let lineCounter = 0;
    const curveType = this.props.windowOptions.curveType ? this.props.windowOptions.curveType : 'curveMonotoneX';

    for (const key in plotData) {
      const xPoints: any = plotData[key].map((cv: string) => parseFloat(cv[0]));
      const yPoints: any = plotData[key].map((cv: string) => parseFloat(cv[1]));

      const yMax = d3.max(yPoints, (d: any) => {
        return +d;
      });
      const indexOfMax = yPoints.indexOf(yMax);

      const line = d3
        .line()
        .x(function (d: any, i: string | number) {
          return xScale(xPoints[i]);
        })
        .y(function (d: any) {
          return yScale(d);
        })
        .curve(d3[curveType]);

      const selectorKey = key.replace(/ /g, '_').replace(/\(|\)/g, '');
      const plot = d3
        .select('#' + config.targetElementID)
        .select('svg')
        .append('path')
        .datum(yPoints)
        .attr('d', line)
        .attr('class', 'plotLine' + selectorKey)
        .attr('stroke', this.colors[lineCounter])
        .attr('fill', 'none')
        .style('opacity', 0)
        .attr('tabindex', 0)
        .attr('stroke-width', 1.5);

      // Aria label is a prop passed to this component and should have 3 parts
      if (
        this.props.windowOptions.ariaLabelForLines &&
        this.props.windowOptions.ariaLabelForLines.length === 3 &&
        yMax !== undefined &&
        yMax !== 0
      ) {
        d3.select('.plotLine' + selectorKey).attr(
          'aria-label',
          'Line plot ' +
            labels[lineCounter] +
            this.props.windowOptions.ariaLabelForLines[0] +
            Math.round(+yMax * 10) / 10 +
            this.props.windowOptions.ariaLabelForLines[1] +
            Math.round(+xPoints[indexOfMax] * 10) / 10 +
            this.props.windowOptions.ariaLabelForLines[2]
        );
      }

      plot.transition().duration(0.5).style('opacity', 1);

      // update line
      plot.datum(yPoints).attr('d', line).style('opacity', 1);

      lineCounter++;
    }
    this.initTooltip(svg, plotData, xScale, yScale, config);
  }

  initTooltip(svg, plotData, xScale, yScale, config) {
    const tooltip = svg.append('g').attr('class', 'tooltip').style('opacity', 0);

    tooltip.append('circle').attr('r', 3).attr('id', 'toolTip').attr('stroke-width', 1.5);

    tooltip.append('rect').attr('id', 'tooltipBox').attr('height', 25).style('opacity', 1).attr('rx', 5).attr('ry', 5);

    tooltip.append('text').attr('id', 'tooltipText').attr('dy', '1.2em').style('text-anchor', 'middle');

    // turn on mouseover
    svg.append('rect')
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .attr('width', this.dimensions.fullWidth)
      .attr('height', this.dimensions.height)
      .on('mouseover', function () {
        tooltip.style('opacity', 1);
      })
      .on('mouseout', function () {
        tooltip.style('opacity', 0).attr('transform', 'translate(-100,0)');
      })
      .on('mousemove', (event: any) => {
        this.plotTooltip(event, plotData, xScale, yScale, tooltip, config.targetElementID);
      });
  }

  plotTooltip(
    event: any,
    plotData: any,
    xScale: { (arg0: any): string; invert: (arg0: any) => any },
    yScale: (arg0: any) => string | number,
    tooltip: {
      attr: (arg0: string, arg1: string) => void;
      select: (
        arg0: string
      ) => {
        (): any;
        new (): any;
        text: { (arg0: string): void; new (): any };
      };
    },
    targetElement: string
  ) {
    // get cursor location and compare with points on graph
    // find closest and display
    const thisWindow: any = d3.select('#' + targetElement);
    const cords = d3.pointer(event, thisWindow.select('svg').node());
    const x0 = cords[0];
    const y0 = cords[1];

    let point: any;
    const diffAry: any = [];
    const pointAry: any = [];

    const bisect = d3.bisector(function (d: any) {
      return d;
    }).left;
    const xValue = xScale.invert(x0);

    let diff = 0;
    for (const key in plotData) {
      const points = plotData[key];
      const xdata = points.map((e: any) => {
        return e[0];
      });
      let idx = bisect(xdata, xValue, 1);
      // confine to point range
      if (idx < 0) {
        idx = 0;
      } else if (idx > points.length - 1) {
        idx = points.length - 1;
      }
      point = points[idx];
      const pointDefined = point !== undefined ? point[1] : 0 //for undefined error check
      diff = Math.abs(+y0 - +yScale(+pointDefined));
      pointAry.push(point);
      diffAry.push(diff);
    }
    const closest = Math.min(...diffAry);
    const idxOfClosest = diffAry.indexOf(closest);
    const closestPoint = pointAry[idxOfClosest];
    if (closestPoint !== undefined) {
      tooltip.attr('transform', 'translate(' + xScale(closestPoint[0]) + ',' + yScale(closestPoint[1]) + ')');
      tooltip.attr('fill', 'black');

      const tooltipOffset = -35;
      const text: any = tooltip
        .select('#tooltipText')
        .text('(' + Math.round(closestPoint[0] * 10) / 10 + ', ' + Math.round(closestPoint[1] * 10) / 10 + ')');

      text.attr('transform', 'translate(' + 0 + ',' + tooltipOffset + ')');

      // text;
      const tooltipTextWidth = thisWindow.select('svg').select('#tooltipText').node().getComputedTextLength() + 10;

      d3.select('#' + targetElement)
        .select('#tooltipBox')
        .attr('width', tooltipTextWidth)
        .attr('x', -(tooltipTextWidth / 2))
        .attr('fill', '#f0f0f0')
        .attr('transform', 'translate(' + 0 + ',' + (tooltipOffset + 3) + ')');
    }
  }

  drawAxisAndLabels(xScale, yScale, hideAxisNumbers: boolean) {
    const yAxis = d3.axisLeft(yScale).scale(yScale);
    const svg = d3.select('#' + this.props.targetDivID).select('svg');
    d3.select('#' + this.props.targetDivID).selectAll('.axis').remove();
    // y axis
    svg
      .append('g')
      .attr('class', 'axis yAxis')
      .attr('id', 'axis')
      .style('stroke-width', 0.25)
      .attr('transform', 'translate(' + this.dimensions.margins.left + ',0)')
      .call(yAxis)
      .selectAll('.tick line')
      .style('stroke', 'gray')
      .style('stroke-width', 1);

    // draw x axis labels
    const xAxis = d3.axisBottom(xScale).scale(xScale);
    // axis
    svg
      .append('g')
      .attr('class', 'xAxis axis')
      .style('stroke-width', 0.25)
      .attr('transform', 'translate(0,' + (this.dimensions.height - this.dimensions.margins.bottom) + ')')
      .call(xAxis);

    if (hideAxisNumbers) {
      svg.selectAll('text').attr('hidden', true);
    }

    const axisXtext = this.props.windowOptions.axis ? this.props.windowOptions.axis.x : 'Time (hr)';
    const axisYtext = this.props.windowOptions.axis ? this.props.windowOptions.axis.y : 'Discharge (cfs)';

    svg
      .append('text')
      .attr('class', 'xAxisLabel')
      .attr('text-anchor', 'middle')
      .attr('transform', 'translate(' + this.dimensions.fullWidth / 2 + ',' + this.dimensions.height + ')')
      .text(axisXtext);

    svg
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0)
      .attr('x', 0 - this.dimensions.height / 2)
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .text(axisYtext);
  }
}
